import { useEffect, useMemo, useState } from 'react';
import useSettings from '../useSettings/useSettings';
import useGetHomeownerAgent from '../../Homeowner/Getters/useGetHomeownerAgent/useGetHomeownerAgent';
import useGetAgentsPersonalData from '../../Agent/Getters/useGetAgentsPersonalData/useGetAgentsPersonalData';

/**
 * If role is Admin, then check if any group has the subscription.
 * If groupId is provided, then check if only the group has the subscription.
 * If checkGroup is false, then check if only the agent has the subscription
 * otherwise, check if the primary group has the subscription for role.
 * If group ID is provided, checkGroup will be ignored
 * @param {string} subscriptionName
 * @param {{groupId?: string, role?: 'agent'|'admin'|'homeowner', checkGroup?: boolean}} [options]
 * @returns {{isGated: boolean; isGettingData: boolean}}
 */
export default function useIsGated(subscriptionName, options) {
	if (!options?.groupId) {
		options = { ...options, checkGroup: true };
	}

	// order of variables matter
	const { checkGroup, groupId, role = 'agent' } = options ?? {};
	const isHomeowner = role === 'homeowner';
	const isAdmin = role === 'admin';
	const { subscriptions } = useSettings();
	const homeownerAgent = useGetHomeownerAgent(!isHomeowner || !checkGroup);
	const userAgent = useGetAgentsPersonalData(role !== 'agent' || !checkGroup);
	const { agent, groups } = isHomeowner ? subscriptions.homeowner : subscriptions; // prettier-ignore
	const [isGettingData, setIsGettingData] = useState(true);
	const [isGated, setIsGated] = useState(!hasSubscription());

	useEffect(() => {
		if (homeownerAgent.isGettingData || userAgent.isGettingData) {
			return;
		}

		setIsGated(!hasSubscription());
		setIsGettingData(false);
	}, [homeownerAgent, userAgent, groupId, subscriptions]);

	// eslint-disable-next-line complexity
	function hasSubscription() {
		if (isAdmin) {
			return [...groups.values()]?.some(
				(flags) => flags?.get(subscriptionName) ?? false
			);
		}

		if (groupId) {
			return groupHasSub(groupId);
		}

		const agentSub = agent.get(subscriptionName) ?? false;
		const groupSub = groupHasSub(getGroupId());

		return agentSub || groupSub || false;
	}

	/**
	 * @param {string} [id]
	 * @returns {boolean}
	 */
	function groupHasSub(id) {
		if (!id) {
			return false;
		}

		return groups.get(id)?.get(subscriptionName) ?? false;
	}

	function getGroupId() {
		if (isHomeowner && homeownerAgent.data) {
			return homeownerAgent.data?.primaryGroup?.id;
		}

		if (!isHomeowner && userAgent.data) {
			return userAgent.data?.groups?.find((group) => group.isPrimary)?.id;
		}

		return '';
	}

	return useMemo(
		() => ({ isGated, isGettingData }),
		[isGated, isGettingData]
	);
}
